import { ChainId, ContractManager, MVXProxyNetworkAddress } from '@ashswap/ash-sdk-js/out';
import { Address, AddressValue, BigUIntValue, BinaryCodec, BooleanValue, BytesValue, CompositeValue, ResultsParser, TokenIdentifierValue, TypedValue, U32Value, U64Value, VariadicValue } from '@multiversx/sdk-core/out';
import { ApiNetworkProvider, ProxyNetworkProvider } from '@multiversx/sdk-network-providers/out';
import { AUTO_COMPOUND_CRON_JOB_TIME, isDev, JEWEL_ASHSWAP_SC_ADDRESS, LATEST_LOAN_SHOW_SIZE } from 'config';
import { getNftsByCollectionFromApi, getTokenPriceFromAshswap, getUserTokenBalance } from 'z/api';
import { convertWeiToEsdt, ERROR_MVX_QUERY_FAILED, toastError, YEAR_IN_SECONDS } from 'z/utils';
import { CommonSettingsType, LendUserType, LoanType, NftWithIndexType, parseLoanStatusEnum, parseLoanTypeEnum, parsePoolStateEnum, parsePoolCurveTypeEnum, parseSwapTypeEnum, NftCollection, NftCollectionPoolInfo, PoolNft, PoolActivity, PoolCurveTypeEnum, parsePoolTypeEnum, LendUserDataType, JewelLendPool, parseJewelLendStateEnum, JewelLendContext, JewelFarmContext, parseJewelFarmStateEnum, JewelFarmPosition, JewelFarmBaseContext, JewelFarmPositionSafetyContext } from '../types';
import { nftLendingSmartContract, nftAmmSmartContract, jewelLendSmartContract, jewelUsdtAshFarmSmartContract, farmAbiRegistry } from './index';
import BigNumber from '@multiversx/sdk-dapp/node_modules/bignumber.js';

export const queryCommonSettings = async (apiAddress: string): Promise<CommonSettingsType | undefined> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const interaction = nftLendingSmartContract.methodsExplicit.viewCommonSettings();
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return undefined;
    }

    const value = firstValue.valueOf();
    // const lendUserData = value.lend_user_data;
    // const decodedLendUserData: LendUserDataType[] = lendUserData.map((item: any) => ({
    //   user_lend_amount: item.user_lend_amount.toFixed(),
    //   user_last_lend_timestamp: item.user_last_lend_timestamp.toNumber(),
    //   user_address: item.user_address.toString(),
    // }));
    const decoded: CommonSettingsType = {
      rewards_pool_rate: value.rewards_pool_rate.toNumber(),
      epunks_buyback_rate: value.epunks_buyback_rate.toNumber(),
      team_rate: value.team_rate.toNumber(),
      epunks_buyback_wallet: value.epunks_buyback_wallet.toString(),
      team_wallet: value.team_wallet.toString(),
      liquidation_wallet: value.liquidation_wallet.toString(),

      lend_epoch_length: value.lend_epoch_length.toNumber(),
      lend_lock_period: value.lend_lock_period.toNumber(),
      liquidation_threshold: value.liquidation_threshold.toNumber(),
      lend_grace_period: value.lend_grace_period.toNumber(),
      liquidation_fee: value.liquidation_fee.toNumber(),

      total_lending_amount: value.total_lending_amount.toFixed(),
      lend_users: value.lend_users.toNumber(),
      current_lend_epoch_id: value.current_lend_epoch_id.toNumber(),
      lend_epoch_start_timestamp: value.lend_epoch_start_timestamp.toNumber(),
      lend_rewards_pool_amount: value.lend_rewards_pool_amount.toFixed(),

      global_liquidation_rate: value.global_liquidation_rate.toNumber(),
      global_max_borrow_amount: value.global_max_borrow_amount.toFixed(),

      borrow_plan_count: value.borrow_plan_count.toNumber(),
      max_borrow_periods: value.max_borrow_periods.map(((v: any) => v.toNumber())),
      borrow_rates: value.borrow_rates.map((v: any) => v.toNumber()),

      accumulated_loan_amount: value.accumulated_loan_amount.toFixed(),
      total_loan_amount: value.total_loan_amount.toFixed(),
      total_collateral_amount: value.total_collateral_amount.toFixed(),
      opened_loan_count: value.opened_loan_count.toNumber(),
      loan_owners: value.loan_owners.toNumber(),
      last_loan_id: value.last_loan_id.toNumber(),

      collection_ids: value.collection_ids.map((v: any) => v.toString()),
      collection_collateral_rates: value.collection_collateral_rates.map((v: any) => v.toNumber()),
      collection_trait_counts: value.collection_trait_counts.map((v: any) => v.toNumber()),
      collection_supplies: value.collection_supplies.map((v: any) => v.toNumber()),

      // lend_user_data: decodedLendUserData,
    };

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return undefined;
  }
};

export const queryViewOpenedLoans = async (apiAddress: string): Promise<LoanType[]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const args: TypedValue[] = [
      new BooleanValue(true), // filter_opened
    ];
    const interaction = nftLendingSmartContract.methodsExplicit.viewLoans(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [];
    }

    const values = firstValue.valueOf();
    const decoded: LoanType[] = values.map((value: any) => ({
      loan_id: value.loan_id.toNumber(),
      loan_type: parseLoanTypeEnum(value.loan_type.name),
      loan_status: parseLoanStatusEnum(value.loan_status.name),
      loan_owner: value.loan_owner.toString(),
      loan_collection_id: value.loan_collection_id.toString(),
      loan_nft_nonce: value.loan_nft_nonce.toNumber(),
      loan_nft_price: value.loan_nft_price.toFixed(),
      loan_borrow_amount: value.loan_borrow_amount.toFixed(),
      loan_debt_amount: value.loan_debt_amount.toFixed(),
      loan_deadline_timestamp: value.loan_deadline_timestamp.toNumber(),
      loan_borrow_plan_id: value.loan_borrow_plan_id.toNumber(),
      loan_open_timestamp: value.loan_open_timestamp.toNumber(),
      loan_close_timestamp: value.loan_close_timestamp.toNumber(),
      loan_nft_valuation: value.loan_nft_valuation.toFixed(),
      loan_liquidation_price: value.loan_liquidation_price.toFixed(),
      loan_cashout: value.loan_cashout.toFixed(),
    }));

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [];
  }
};

export const queryViewLoans = async (apiAddress: string): Promise<LoanType[]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const args: TypedValue[] = [
      new BooleanValue(false), // filter_opened
    ];
    const interaction = nftLendingSmartContract.methodsExplicit.viewLoans(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [];
    }

    const values = firstValue.valueOf();
    const decoded: LoanType[] = values.map((value: any) => ({
      loan_id: value.loan_id.toNumber(),
      loan_type: parseLoanTypeEnum(value.loan_type.name),
      loan_status: parseLoanStatusEnum(value.loan_status.name),
      loan_owner: value.loan_owner.toString(),
      loan_collection_id: value.loan_collection_id.toString(),
      loan_nft_nonce: value.loan_nft_nonce.toNumber(),
      loan_nft_price: value.loan_nft_price.toFixed(),
      loan_borrow_amount: value.loan_borrow_amount.toFixed(),
      loan_debt_amount: value.loan_debt_amount.toFixed(),
      loan_deadline_timestamp: value.loan_deadline_timestamp.toNumber(),
      loan_borrow_plan_id: value.loan_borrow_plan_id.toNumber(),
      loan_open_timestamp: value.loan_open_timestamp.toNumber(),
      loan_close_timestamp: value.loan_close_timestamp.toNumber(),
      loan_nft_valuation: value.loan_nft_valuation.toFixed(),
      loan_liquidation_price: value.loan_liquidation_price.toFixed(),
      loan_cashout: value.loan_cashout.toFixed(),
    }));

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [];
  }
};

export const queryViewUser = async (apiAddress: string, address: string): Promise<LendUserType | undefined> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const args: TypedValue[] = [
      new AddressValue(new Address(address)),
    ];
    const interaction = nftLendingSmartContract.methodsExplicit.viewUser(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return undefined;
    }
    const value = firstValue.valueOf();
    const decoded: LendUserType = {
      user_lend_amount: value.user_lend_amount.toFixed(),
      user_last_lend_timestamp: value.user_last_lend_timestamp.toNumber(),
      lender_rewards_amounts: value.lender_rewards_amounts.map((v: any) => v.toFixed()),
      lender_rewards_claimeds: value.lender_rewards_claimeds,

      user_loan_ids: value.user_loan_ids.map((v: any) => v.toNumber()),
      user_borrow_amount: value.user_borrow_amount.toFixed(),
      user_debt_amount: value.user_debt_amount.toFixed(),
    };

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return undefined;
  }
};

export const queryViewLoansById = async (apiAddress: string, loanIds: number[]): Promise<LoanType[]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const ids: TypedValue[] = loanIds.map(v => new U32Value(v));
    const args: TypedValue[] = [
      VariadicValue.fromItems(...ids),
    ];
    const interaction = nftLendingSmartContract.methodsExplicit.viewLoansById(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [];
    }
    const values = firstValue.valueOf();
    const decoded: LoanType[] = values.map((value: any, index: number) => ({
      index,
      loan_id: value.loan_id.toNumber(),
      loan_type: parseLoanTypeEnum(value.loan_type.name),
      loan_status: parseLoanStatusEnum(value.loan_status.name),
      loan_owner: value.loan_owner.toString(),
      loan_collection_id: value.loan_collection_id.toString(),
      loan_nft_nonce: value.loan_nft_nonce.toNumber(),
      loan_nft_price: value.loan_nft_price.toFixed(),
      loan_borrow_amount: value.loan_borrow_amount.toFixed(),
      loan_debt_amount: value.loan_debt_amount.toFixed(),
      loan_deadline_timestamp: value.loan_deadline_timestamp.toNumber(),
      loan_borrow_plan_id: value.loan_borrow_plan_id.toNumber(),
      loan_open_timestamp: value.loan_open_timestamp.toNumber(),
      loan_close_timestamp: value.loan_close_timestamp.toNumber(),
      loan_nft_valuation: value.loan_nft_valuation.toFixed(),
      loan_liquidation_price: value.loan_liquidation_price.toFixed(),
      loan_cashout: value.loan_cashout.toFixed(),
    }));

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [];
  }
};

export const queryViewLatestLoans = async (apiAddress: string, size: number): Promise<LoanType[]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const args: TypedValue[] = [
      new U32Value(size)
    ];
    const interaction = nftLendingSmartContract.methodsExplicit.viewLatestLoans(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [];
    }
    const values = firstValue.valueOf();
    const decoded: LoanType[] = values.map((value: any, index: number) => ({
      index,
      loan_id: value.loan_id.toNumber(),
      loan_type: parseLoanTypeEnum(value.loan_type.name),
      loan_status: parseLoanStatusEnum(value.loan_status.name),
      loan_owner: value.loan_owner.toString(),
      loan_collection_id: value.loan_collection_id.toString(),
      loan_nft_nonce: value.loan_nft_nonce.toNumber(),
      loan_nft_price: value.loan_nft_price.toFixed(),
      loan_borrow_amount: value.loan_borrow_amount.toFixed(),
      loan_debt_amount: value.loan_debt_amount.toFixed(),
      loan_deadline_timestamp: value.loan_deadline_timestamp.toNumber(),
      loan_borrow_plan_id: value.loan_borrow_plan_id.toNumber(),
      loan_open_timestamp: value.loan_open_timestamp.toNumber(),
      loan_close_timestamp: value.loan_close_timestamp.toNumber(),
      loan_nft_valuation: value.loan_nft_valuation.toFixed(),
      loan_liquidation_price: value.loan_liquidation_price.toFixed(),
      loan_cashout: value.loan_cashout.toFixed(),
    }));

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [];
  }
};

export const queryComputeNftPrices = async (apiAddress: string, nfts: any): Promise<string[]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);

    const items: TypedValue[] = [];
    nfts.map((nft: any) => items.push(
      CompositeValue.fromItems(
        new TokenIdentifierValue(nft.collection),
        new U64Value(nft.nonce),
      )
    ));
    const args: TypedValue[] = [VariadicValue.fromItems(...items)];

    const interaction = nftLendingSmartContract.methodsExplicit.computeNftPrices(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [];
    }

    const values = firstValue.valueOf();
    const decoded: string[] = values.map((value: any) => value.toFixed());

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [];
  }
};

// AMM
export const queryViewCollections = async (apiAddress: string): Promise<NftCollection[]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const interaction = nftAmmSmartContract.methodsExplicit.getNftCollections();
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [];
    }
    const values = firstValue.valueOf();
    const decoded: NftCollection[] = values.map((value: any, index: number) => ({
      index,
      nft_token_id: value.nft_token_id.toString(),
      nft_listed_count: value.nft_listed_count.toNumber(),
      nft_floor_price: value.nft_floor_price.toFixed(),
      nft_best_offer_price: value.nft_best_offer_price.toFixed(),
      nft_offer_tvl: value.nft_offer_tvl.toFixed(),
      nft_collection_trading_volume: value.nft_collection_trading_volume.toFixed(),
    }));

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [];
  }
};

export const queryViewCommonSettings = async (apiAddress: string): Promise<[NftCollection[], PoolActivity[]]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress, { timeout: 12000 });
    const args: TypedValue[] = [
      new U32Value(LATEST_LOAN_SHOW_SIZE)
    ];
    const interaction = nftAmmSmartContract.methodsExplicit.getCommonSettings(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [[], []];
    }
    const values = firstValue.valueOf();
    const nftCollections = values.nft_collections;
    const latest_activities = values.latest_activities;
    const decodedNftCollections: NftCollection[] = nftCollections.map((value: any, index: number) => ({
      index,
      nft_token_id: value.nft_token_id.toString(),
      nft_listed_count: value.nft_listed_count.toNumber(),
      nft_floor_price: value.nft_floor_price.toFixed(),
      nft_best_offer_price: value.nft_best_offer_price.toFixed(),
      nft_offer_tvl: value.nft_offer_tvl.toFixed(),
      nft_collection_trading_volume: value.nft_collection_trading_volume.toFixed(),
    }));

    let decodedPoolActivity: PoolActivity[] = latest_activities.map((value: any, index: number) => ({
      index,
      pool_nft_token_id: value.nft_token_id.toString(),
      pool_id: value.pool_id.toNumber(),
      pool_activity_id: value.pool_activity_id.toNumber(),
      pool_activity_token_nonce: value.pool_activity_token_nonce.toNumber(),
      pool_activity_token_amount: value.pool_activity_token_amount.toFixed(),
      pool_activity_caller_address: value.pool_activity_caller_address.toString(),
      pool_activity_type: parseSwapTypeEnum(value.pool_activity_type.name),
      pool_activity_price: value.pool_activity_price.toFixed(),
      pool_activity_timestamp: value.pool_activity_timestamp.toNumber(),
    }));
    decodedPoolActivity.sort((a: PoolActivity, b: PoolActivity) => b.pool_activity_timestamp - a.pool_activity_timestamp);
    if (decodedPoolActivity.length > LATEST_LOAN_SHOW_SIZE) {
      decodedPoolActivity = decodedPoolActivity.slice(0, LATEST_LOAN_SHOW_SIZE);
    }

    return [decodedNftCollections, decodedPoolActivity];
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [[], []];
  }
};

export const queryViewCollectionData = async (apiAddress: string, pool_nft_token_id: string): Promise<[NftCollectionPoolInfo[], PoolNft[]]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const args: TypedValue[] = [
      new TokenIdentifierValue(pool_nft_token_id)
    ];
    const interaction = nftAmmSmartContract.methodsExplicit.getCollectionData(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [[], []];
    }
    const values = firstValue.valueOf();
    const collectionPools = values.collection_pools;
    const collectionNfts = values.collection_nfts;
    const decodedCollectionPools: NftCollectionPoolInfo[] = collectionPools.map((value: any, index: number) => {
      const data = {
        index,
        pool_nft_token_id: value.pool_nft_token_id.toString(),
        pool_id: value.pool_id.toNumber(),
        pool_owner: value.pool_owner.toString(),
        pool_state: parsePoolStateEnum(value.pool_state.name),
        pool_type: parsePoolTypeEnum(value.pool_type.name),
        pool_trade_fee_percent: value.pool_trade_fee_percent.toNumber() / 100 + '%',
        pool_spot_price: value.pool_spot_price.toFixed(),
        pool_curve_type: parsePoolCurveTypeEnum(value.pool_curve_type.name),
        pool_linear_curve_step_amount: value.pool_linear_curve_step_amount.toFixed(),
        pool_exponential_curve_step_percent: value.pool_exponential_curve_step_percent.toNumber() / 100,
        pool_accumulated_fee: value.pool_accumulated_fee.toFixed(),
        pool_swap_token_id: value.pool_swap_token_id.toString(),
        pool_swap_token_decimal: value.pool_swap_token_decimal.toNumber(),
        pool_swap_token_reserve: value.pool_swap_token_reserve.toFixed(),
        pool_buy_order_count: value.pool_buy_order_count.toNumber(),
        pool_nft_token_reserve: value.pool_nft_token_reserve.toNumber(),
        pool_spot_sell_price: value.pool_spot_sell_price.toFixed(),
        pool_trade_buy_price: value.pool_trade_buy_price.toFixed(),
        pool_trade_sell_price: value.pool_trade_sell_price.toFixed(),
        service_fee: value.service_fee.toNumber() / 100,
        pool_delta: value.pool_linear_curve_step_amount.toFixed(),
      };
      if (data.pool_curve_type == PoolCurveTypeEnum.Exponential) {
        data.pool_delta = data.pool_exponential_curve_step_percent + '%';
      }
      return data;
    });

    const decodedCollectionNfts: PoolNft[] = collectionNfts.map((value: any, index: number) => ({
      index,
      pool_id: value.pool_id.toNumber(),
      nft_token_id: value.nft_token_id.toString(),
      nft_token_nonce: value.nft_token_nonce.toNumber(),
      nft_token_amount: value.nft_token_amount.toFixed(),
      pool_spot_buy_price: value.pool_spot_buy_price.toFixed(),
      pool_spot_sell_price: value.pool_spot_sell_price.toFixed(),
      pool_trade_buy_price: value.pool_trade_buy_price.toFixed(),
      pool_trade_sell_price: value.pool_trade_sell_price.toFixed(),
    }));

    decodedCollectionNfts.sort((a: PoolNft, b: PoolNft) => convertWeiToEsdt(a.pool_trade_sell_price).toNumber() - convertWeiToEsdt(b.pool_trade_sell_price).toNumber());

    return [decodedCollectionPools, decodedCollectionNfts];
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [[], []];
  }
};

export const queryViewPoolData = async (apiAddress: string, pool_nft_token_id: string, poolId: number): Promise<[NftCollectionPoolInfo | undefined, PoolNft[]]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const args: TypedValue[] = [
      new TokenIdentifierValue(pool_nft_token_id),
      new U32Value(poolId)
    ];
    const interaction = nftAmmSmartContract.methodsExplicit.getPoolData(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [undefined, []];
    }
    const values = firstValue.valueOf();
    const poolInfo = values.pool_info;
    const poolNfts = values.pool_nfts;
    const decodedPoolInfo: NftCollectionPoolInfo = {
      pool_nft_token_id: poolInfo.pool_nft_token_id.toString(),
      pool_id: poolInfo.pool_id.toNumber(),
      pool_owner: poolInfo.pool_owner.toString(),
      pool_state: parsePoolStateEnum(poolInfo.pool_state.name),
      pool_type: parsePoolTypeEnum(poolInfo.pool_type.name),
      pool_trade_fee_percent: poolInfo.pool_trade_fee_percent.toNumber() / 100,
      pool_spot_price: poolInfo.pool_spot_price.toFixed(),
      pool_curve_type: parsePoolCurveTypeEnum(poolInfo.pool_curve_type.name),
      pool_linear_curve_step_amount: poolInfo.pool_linear_curve_step_amount.toFixed(),
      pool_exponential_curve_step_percent: poolInfo.pool_exponential_curve_step_percent.toNumber() / 100,
      pool_accumulated_fee: poolInfo.pool_accumulated_fee.toFixed(),
      pool_swap_token_id: poolInfo.pool_swap_token_id.toString(),
      pool_swap_token_decimal: poolInfo.pool_swap_token_decimal.toNumber(),
      pool_swap_token_reserve: poolInfo.pool_swap_token_reserve.toFixed(),
      pool_buy_order_count: poolInfo.pool_buy_order_count.toNumber(),
      pool_nft_token_reserve: poolInfo.pool_nft_token_reserve.toNumber(),
      pool_spot_sell_price: poolInfo.pool_spot_sell_price.toFixed(),
      pool_trade_buy_price: poolInfo.pool_trade_buy_price.toFixed(),
      pool_trade_sell_price: poolInfo.pool_trade_sell_price.toFixed(),
      service_fee: poolInfo.service_fee.toNumber() / 100,
      pool_delta: poolInfo.pool_linear_curve_step_amount.toFixed(),
    };

    const decodedPoolNfts: PoolNft[] = poolNfts.map((value: any, index: number) => ({
      index,
      pool_id: value.pool_id.toNumber(),
      nft_token_id: value.nft_token_id.toString(),
      nft_token_nonce: value.nft_token_nonce.toNumber(),
      nft_token_amount: value.nft_token_amount.toFixed(),
      pool_spot_buy_price: value.pool_spot_buy_price.toFixed(),
      pool_spot_sell_price: value.pool_spot_sell_price.toFixed(),
      pool_trade_buy_price: value.pool_trade_buy_price.toFixed(),
      pool_trade_sell_price: value.pool_trade_sell_price.toFixed(),
    }));

    return [decodedPoolInfo, decodedPoolNfts];
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [undefined, []];
  }
};

export const queryViewCollectionNfts = async (apiAddress: string, pool_nft_token_id: string): Promise<PoolNft[]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const args: TypedValue[] = [
      new TokenIdentifierValue(pool_nft_token_id)
    ];
    const interaction = nftAmmSmartContract.methodsExplicit.getCollectionNfts(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [];
    }
    const values = firstValue.valueOf();
    const decoded: PoolNft[] = values.map((value: any, index: number) => ({
      index,
      pool_id: value.pool_id.toNumber(),
      nft_token_id: value.nft_token_id.toString(),
      nft_token_nonce: value.nft_token_nonce.toNumber(),
      nft_token_amount: value.nft_token_amount.toFixed(),
      pool_spot_buy_price: value.pool_spot_buy_price.toFixed(),
      pool_spot_sell_price: value.pool_spot_sell_price.toFixed(),
      pool_trade_buy_price: value.pool_trade_buy_price.toFixed(),
      pool_trade_sell_price: value.pool_trade_sell_price.toFixed(),
    }));

    decoded.sort((a: PoolNft, b: PoolNft) => convertWeiToEsdt(a.pool_trade_sell_price).toNumber() - convertWeiToEsdt(b.pool_trade_sell_price).toNumber());
    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [];
  }
};

export const queryViewCollectionPools = async (apiAddress: string, pool_nft_token_id: string): Promise<NftCollectionPoolInfo[]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const args: TypedValue[] = [
      new TokenIdentifierValue(pool_nft_token_id)
    ];
    const interaction = nftAmmSmartContract.methodsExplicit.getNftCollectionPools(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [];
    }
    const values = firstValue.valueOf();
    const decoded: NftCollectionPoolInfo[] = values.map((value: any, index: number) => {
      const data = {
        index,
        pool_nft_token_id: value.pool_nft_token_id.toString(),
        pool_id: value.pool_id.toNumber(),
        pool_owner: value.pool_owner.toString(),
        pool_state: parsePoolStateEnum(value.pool_state.name),
        pool_type: parsePoolTypeEnum(value.pool_type.name),
        pool_trade_fee_percent: value.pool_trade_fee_percent.toNumber() / 100 + '%',
        pool_spot_price: value.pool_spot_price.toFixed(),
        pool_curve_type: parsePoolCurveTypeEnum(value.pool_curve_type.name),
        pool_linear_curve_step_amount: value.pool_linear_curve_step_amount.toFixed(),
        pool_exponential_curve_step_percent: value.pool_exponential_curve_step_percent.toNumber() / 100,
        pool_accumulated_fee: value.pool_accumulated_fee.toFixed(),
        pool_swap_token_id: value.pool_swap_token_id.toString(),
        pool_swap_token_decimal: value.pool_swap_token_decimal.toNumber(),
        pool_swap_token_reserve: value.pool_swap_token_reserve.toFixed(),
        pool_buy_order_count: value.pool_buy_order_count.toNumber(),
        pool_nft_token_reserve: value.pool_nft_token_reserve.toNumber(),
        pool_spot_sell_price: value.pool_spot_sell_price.toFixed(),
        pool_trade_buy_price: value.pool_trade_buy_price.toFixed(),
        pool_trade_sell_price: value.pool_trade_sell_price.toFixed(),
        service_fee: value.service_fee.toNumber() / 100,
        pool_delta: value.pool_linear_curve_step_amount.toFixed(),
      };
      if (data.pool_curve_type == PoolCurveTypeEnum.Exponential) {
        data.pool_delta = data.pool_exponential_curve_step_percent + '%';
      }
      return data;
    });

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [];
  }
};

export const queryViewOwnPools = async (apiAddress: string, address: string): Promise<NftCollectionPoolInfo[]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const args: TypedValue[] = [
      new AddressValue(new Address(address))
    ];
    const interaction = nftAmmSmartContract.methodsExplicit.getOwnPools(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [];
    }
    const values = firstValue.valueOf();
    const decoded: NftCollectionPoolInfo[] = values.map((value: any, index: number) => {
      const data = {
        index,
        pool_nft_token_id: value.pool_nft_token_id.toString(),
        pool_id: value.pool_id.toNumber(),
        pool_owner: value.pool_owner.toString(),
        pool_state: parsePoolStateEnum(value.pool_state.name),
        pool_type: parsePoolTypeEnum(value.pool_type.name),
        pool_trade_fee_percent: value.pool_trade_fee_percent.toNumber() / 100 + '%',
        pool_spot_price: value.pool_spot_price.toFixed(),
        pool_curve_type: parsePoolCurveTypeEnum(value.pool_curve_type.name),
        pool_linear_curve_step_amount: value.pool_linear_curve_step_amount.toFixed(),
        pool_exponential_curve_step_percent: value.pool_exponential_curve_step_percent.toNumber() / 100,
        pool_accumulated_fee: value.pool_accumulated_fee.toFixed(),
        pool_swap_token_id: value.pool_swap_token_id.toString(),
        pool_swap_token_decimal: value.pool_swap_token_decimal.toNumber(),
        pool_swap_token_reserve: value.pool_swap_token_reserve.toFixed(),
        pool_buy_order_count: value.pool_buy_order_count.toNumber(),
        pool_nft_token_reserve: value.pool_nft_token_reserve.toNumber(),
        pool_spot_sell_price: value.pool_spot_sell_price.toFixed(),
        pool_trade_buy_price: value.pool_trade_buy_price.toFixed(),
        pool_trade_sell_price: value.pool_trade_sell_price.toFixed(),
        service_fee: value.service_fee.toNumber() / 100,
        pool_delta: value.pool_linear_curve_step_amount.toFixed(),
      };
      if (data.pool_curve_type == PoolCurveTypeEnum.Exponential) {
        data.pool_delta = data.pool_exponential_curve_step_percent + '%';
      }
      return data;
    });

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [];
  }
};

export const queryViewCollectionActivities = async (apiAddress: string, pool_nft_token_id: string): Promise<PoolActivity[]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress, { timeout: 12000 });
    const args: TypedValue[] = [
      new TokenIdentifierValue(pool_nft_token_id)
    ];
    const interaction = nftAmmSmartContract.methodsExplicit.getCollectionActivities(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [];
    }
    const values = firstValue.valueOf();
    const decoded: PoolActivity[] = values.map((value: any, index: number) => ({
      index,
      pool_nft_token_id: value.nft_token_id.toString(),
      pool_id: value.pool_id.toNumber(),
      pool_activity_id: value.pool_activity_id.toNumber(),
      pool_activity_token_nonce: value.pool_activity_token_nonce.toNumber(),
      pool_activity_token_amount: value.pool_activity_token_amount.toFixed(),
      pool_activity_caller_address: value.pool_activity_caller_address.toString(),
      pool_activity_type: parseSwapTypeEnum(value.pool_activity_type.name),
      pool_activity_price: value.pool_activity_price.toFixed(),
      pool_activity_timestamp: value.pool_activity_timestamp.toNumber(),
    }));
    decoded.sort((a: PoolActivity, b: PoolActivity) => (a.pool_activity_timestamp > b.pool_activity_timestamp) ? -1 : 1);

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [];
  }
};

export const queryViewCollectionPool = async (apiAddress: string, pool_nft_token_id: string, poolId: number): Promise<NftCollectionPoolInfo | undefined> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const args: TypedValue[] = [
      new TokenIdentifierValue(pool_nft_token_id),
      new U32Value(poolId)
    ];
    const interaction = nftAmmSmartContract.methodsExplicit.getNftCollectionPool(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return undefined;
    }
    const value = firstValue.valueOf();
    const decoded: NftCollectionPoolInfo = {
      pool_nft_token_id: value.pool_nft_token_id.toString(),
      pool_id: value.pool_id.toNumber(),
      pool_owner: value.pool_owner.toString(),
      pool_state: parsePoolStateEnum(value.pool_state.name),
      pool_type: parsePoolTypeEnum(value.pool_type.name),
      pool_trade_fee_percent: value.pool_trade_fee_percent.toNumber() / 100,
      pool_spot_price: value.pool_spot_price.toFixed(),
      pool_curve_type: parsePoolCurveTypeEnum(value.pool_curve_type.name),
      pool_linear_curve_step_amount: value.pool_linear_curve_step_amount.toFixed(),
      pool_exponential_curve_step_percent: value.pool_exponential_curve_step_percent.toNumber() / 100,
      pool_accumulated_fee: value.pool_accumulated_fee.toFixed(),
      pool_swap_token_id: value.pool_swap_token_id.toString(),
      pool_swap_token_decimal: value.pool_swap_token_decimal.toNumber(),
      pool_swap_token_reserve: value.pool_swap_token_reserve.toFixed(),
      pool_buy_order_count: value.pool_buy_order_count.toNumber(),
      pool_nft_token_reserve: value.pool_nft_token_reserve.toNumber(),
      pool_spot_sell_price: value.pool_spot_sell_price.toFixed(),
      pool_trade_buy_price: value.pool_trade_buy_price.toFixed(),
      pool_trade_sell_price: value.pool_trade_sell_price.toFixed(),
      service_fee: value.service_fee.toNumber() / 100,
      pool_delta: value.pool_linear_curve_step_amount.toFixed(),
    };

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return undefined;
  }
};

export const queryViewPoolNfts = async (apiAddress: string, pool_nft_token_id: string, poolId: number): Promise<PoolNft[]> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const args: TypedValue[] = [
      new TokenIdentifierValue(pool_nft_token_id),
      new U32Value(poolId)
    ];
    const interaction = nftAmmSmartContract.methodsExplicit.getPoolNfts(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [];
    }
    const values = firstValue.valueOf();
    const decoded: PoolNft[] = values.map((value: any, index: number) => ({
      index,
      pool_id: value.pool_id.toNumber(),
      nft_token_id: value.nft_token_id.toString(),
      nft_token_nonce: value.nft_token_nonce.toNumber(),
      nft_token_amount: value.nft_token_amount.toFixed(),
      pool_spot_buy_price: value.pool_spot_buy_price.toFixed(),
      pool_spot_sell_price: value.pool_spot_sell_price.toFixed(),
      pool_trade_buy_price: value.pool_trade_buy_price.toFixed(),
      pool_trade_sell_price: value.pool_trade_sell_price.toFixed(),
    }));

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [];
  }
};

///////////////// FARM /////////////////
export const queryViewJewelLendContext = async (apiAddress: string, address: string, balance: string): Promise<JewelLendContext | undefined> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const interaction = jewelLendSmartContract.methodsExplicit.viewJewelLendContext();
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return undefined;
    }

    const value = firstValue.valueOf();

    const admins = value.admins;
    const decodedAdmins: string[] = admins.map((item: any) => item.toString());
    const borrowWhitelists = value.borrow_whitelist;
    const decodedBorrowWhitelists: string[] = borrowWhitelists.map((item: any) => item.toString());

    const depositTokenIds = value.deposit_token_ids;
    const ibTokenIds = value.ib_token_ids;
    const depositTokenAmounts = value.deposit_token_amounts;
    const ibTokenAmounts = value.ib_token_amounts;
    const loanAmounts = value.loan_amounts;

    const userTokens: any = [];
    if (address) {
      const userTokenIds: string[] = [];
      for (let i = 0; i < depositTokenIds.length; i++) {
        userTokenIds.push(depositTokenIds[i].toString());
        userTokenIds.push(ibTokenIds[i].toString());
      }

      const _userTokens = await getUserTokenBalance(apiAddress, address, userTokenIds);

      userTokens['EGLD'] = {
        balance: balance,
      };

      for (let i = 0; i < _userTokens.length; i++) {
        userTokens[_userTokens[i].identifier] = {
          balance: _userTokens[i].balance,
        };
      }

      for (let i = 0; i < userTokenIds.length; i++) {
        if (!userTokens[userTokenIds[i]]) {
          userTokens[userTokenIds[i]] = {
            balance: '0',
          };
        }
      }
    }

    const lendPool: JewelLendPool[] = [];
    for (let i = 0; i < depositTokenIds.length; i++) {
      const pool: JewelLendPool = {
        deposit_token_id: depositTokenIds[i].toString(),
        ib_token_id: ibTokenIds[i].toString(),
        deposit_token_amount: depositTokenAmounts[i].toFixed(),
        ib_token_amount: ibTokenAmounts[i].toFixed(),
        loan_amount: loanAmounts[i].toFixed(),
        user_deposit_token_balance: address ? userTokens[depositTokenIds[i].toString()].balance : '0',
        user_ib_token_balance: address ? userTokens[ibTokenIds[i].toString()].balance : '0',
      };
      lendPool.push(pool);
    }

    const decodedValue: JewelLendContext = {
      state: parseJewelLendStateEnum(value.state.name),
      admins: decodedAdmins,
      borrow_whitelist: decodedBorrowWhitelists,
      lendPool: lendPool,
    };

    return decodedValue;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return undefined;
  }
};

export const queryGetEquivalent = async (apiAddress: string, tokenId: string, tokenAmount: string): Promise<string> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const args: TypedValue[] = [
      new TokenIdentifierValue(tokenId),
      new BigUIntValue(tokenAmount)
    ];
    const interaction = jewelLendSmartContract.methodsExplicit.getEquivalent(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return '0';
    }

    const value = firstValue.valueOf();
    return value.toFixed();
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return '0';
  }
};

export const queryViewJewelFarmBaseContext = async (apiAddress: string, address: string, balance: string): Promise<JewelFarmBaseContext | undefined> => {
  try {
    const networkProvider = new ApiNetworkProvider(apiAddress);
    const interaction = jewelUsdtAshFarmSmartContract.methodsExplicit.viewBaseContext();
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return undefined;
    }

    const value = firstValue.valueOf();

    const userTokens: any = [];
    if (address) {
      const userTokenIds: string[] = [];
      for (let i = 0; i < value.liquidation_tokens.length; i++) {
        userTokenIds.push(value.liquidation_tokens[i].toString());
      }
      userTokenIds.push(value.reward_token_id.toString());
      userTokenIds.push(value.wegld_token_id.toString());

      for (let i = 0; i < value.farms.length; i++) {
        const farm = value.farms[i];
        for (let j = 0; j < farm.token_ids.length; j++) {
          userTokenIds.push(farm.token_ids[j].toString());
        }
        userTokenIds.push(farm.lp_token_id.toString());
        userTokenIds.push(farm.farm_token_id.toString());
      }

      const _userTokens = await getUserTokenBalance(apiAddress, address, userTokenIds);

      userTokens['EGLD'] = {
        balance: balance,
      };

      for (let i = 0; i < _userTokens.length; i++) {
        userTokens[_userTokens[i].identifier] = {
          balance: _userTokens[i].balance,
        };
      }

      for (let i = 0; i < userTokenIds.length; i++) {
        if (!userTokens[userTokenIds[i]]) {
          userTokens[userTokenIds[i]] = {
            balance: '0',
          };
        }
      }
    }

    const _tokens = await getTokenPriceFromAshswap();

    const decodedFarms: JewelFarmContext[] = [];
    for (let i = 0; i < value.farms.length; i++) {
      const item = value.farms[i];

      const farm_token_id = item.farm_token_id.toString();
      const lp_token_id = item.lp_token_id.toString();
      const dex_pool_address = item.dex_pool_address.toString();
      const dex_farm_address = item.dex_farm_address.toString();
      const nfts = await getNftsByCollectionFromApi(apiAddress, JEWEL_ASHSWAP_SC_ADDRESS, farm_token_id);
      let _stakedLpAmount = '0';
      let _lpPrice = '0';
      let _reserves: any[] = [];
      let _lpTotalSupply = new BigNumber('0');
      let _apy = 0;
      if (nfts.length > 0) {
        const nft = nfts[0];
        const farmTokenAttributesStruct = farmAbiRegistry.getStruct("FarmTokenAttributes");
        const decodedAttributes = new BinaryCodec().decodeTopLevel(Buffer.from(nft.attributes, "base64"), farmTokenAttributesStruct).valueOf();
        _stakedLpAmount = decodedAttributes.initial_farming_amount.toFixed();

        if (_tokens.data) {
          const _prices = _tokens.data.tokens;
          _lpPrice = _prices.filter((token: any) => token.id === lp_token_id)[0]['price'];
        }

        const proxy = new ProxyNetworkProvider(isDev ? MVXProxyNetworkAddress.Devnet : MVXProxyNetworkAddress.Mainnet);
        const poolContract = ContractManager.getPoolV2Contract(
          dex_pool_address
        ).onChain(isDev ? ChainId.Devnet : ChainId.Mainnet).onProxy(proxy);
        _reserves = await poolContract.getReserves();
        _lpTotalSupply = await poolContract.getTotalSupply();

        const farmContract = ContractManager
          .getFarmContract(dex_farm_address)
          .onChain(ChainId.Devnet)
          .onProxy(proxy);

        const rewardsAmount = await farmContract.calculateRewardsForGivenPosition(new BigNumber(nft.balance), decodedAttributes);
        const apr = rewardsAmount.multipliedBy(YEAR_IN_SECONDS).dividedBy(AUTO_COMPOUND_CRON_JOB_TIME).dividedBy(_stakedLpAmount).dividedBy(100);
        const n = YEAR_IN_SECONDS / AUTO_COMPOUND_CRON_JOB_TIME;
        _apy = Math.pow((apr.toNumber() / n) + 1, n) - 1;
      }

      const farm = {
        farm_id: item.farm_id.toNumber(),
        dex_pool_address: dex_pool_address,
        dex_farm_address: item.dex_farm_address.toString(),
        token_ids: item.token_ids.map((item: any) => item.toString()),
        lp_token_id: lp_token_id,
        farm_token_id: farm_token_id,
        farm_token_nonce: item.farm_token_nonce.toNumber(),
        farm_token_amount: item.farm_token_amount.toFixed(),
        pending_reward_token_amount: item.pending_reward_token_amount.toFixed(),
        borrow_amounts: item.borrow_amounts.map((item: any) => item.toFixed()),
        farm_shares_per_token: item.farm_shares_per_token.map((item: any) => item.toFixed()),
        total_farm_share: item.total_farm_share.toFixed(),
        opened_position_count: item.opened_position_count.toNumber(),
        staked_lp_amount: _stakedLpAmount,
        lp_price: _lpPrice,
        reserves: _reserves.map((item: any) => item.toFixed()),
        lp_total_supply: _lpTotalSupply.toFixed(),
        apy: _apy * 100,
      };

      decodedFarms.push(farm);
    }

    const decodedValue: JewelFarmBaseContext = {
      state: parseJewelFarmStateEnum(value.state.name),
      admins: value.admins.map((item: any) => item.toString()),
      jewel_lend_address: value.jewel_lend_address.toString(),
      reward_token_id: value.reward_token_id.toString(),
      wegld_token_id: value.wegld_token_id.toString(),
      ashswap_governance_address: value.ashswap_governance_address.toString(),
      ashswap_fee_distributor_address: value.ashswap_fee_distributor_address.toString(),
      ashswap_pool_addresses: value.ashswap_pool_addresses.map((item: any) => item.toString()),
      xexchange_pool_addresses: value.xexchange_pool_addresses.map((item: any) => item.toString()),
      min_compound_rewards_amount: value.min_compound_rewards_amount.toFixed(),
      liquidation_tokens: value.liquidation_tokens.map((item: any) => item.toString()),
      liquidation_thresholds: value.liquidation_thresholds.map((item: any) => item.toNumber()),
      min_deposit_tokens: value.min_deposit_tokens.map((item: any) => item.toString()),
      min_deposit_values: value.min_deposit_values.map((item: any) => item.toFixed()),
      fee_collector_addresses: value.fee_collector_addresses.map((item: any) => item.toString()),
      fee_collector_percents: value.fee_collector_percents.map((item: any) => item.toNumber()),
      farms: decodedFarms,
      last_position_id: value.last_position_id.toNumber(),
      total_opened_position_count: value.total_opened_position_count.toNumber(),
      user_tokens: userTokens,
      ashswap_tokens: _tokens,
    };

    return decodedValue;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return undefined;
  }
};

export const queryViewPositionsByIds = async (apiAddress: string, lastPostionId: number): Promise<JewelFarmPosition[]> => {
  try {
    const items = [];
    for (let i = 1; i <= lastPostionId; i++) {
      items.push(new U32Value(i));
    }
    const args: TypedValue[] = [
      VariadicValue.fromItems(...items),
    ];

    const networkProvider = new ApiNetworkProvider(apiAddress);
    const interaction = jewelUsdtAshFarmSmartContract.methodsExplicit.viewPositionsByIds(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [];
    }

    const values = firstValue.valueOf();
    const decoded: JewelFarmPosition[] = values.map((value: any) => {
      return {
        position_id: value.position_id.toNumber(),
        position_farm_id: value.position_farm_id.toNumber(),
        position_state: value.position_state.name,
        position_owner: value.position_owner.toString(),
        position_leverage_percent: value.position_leverage_percent.toNumber(),
        position_debt_token: value.position_debt_token.toString(),
        position_debt_amount: value.position_debt_amount.toFixed(),
        position_farm_share: value.position_farm_share.toFixed(),
        position_open_timestamp: value.position_open_timestamp.toNumber(),
        position_deposit_payments: value.position_deposit_payments.map((item: any) => ({
          token_identifier: item.token_identifier.toString(),
          token_nonce: item.token_nonce.toString(),
          amount: item.amount.toFixed(),
        }))
      };
    });

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [];
  }
};

export const queryViewPositionsSafetyContextByIds = async (apiAddress: string, positionIds: number[]): Promise<JewelFarmPositionSafetyContext[]> => {
  try {
    const items = [];
    for (let i = 0; i < positionIds.length; i++) {
      items.push(new U32Value(positionIds[i]));
    }
    const args: TypedValue[] = [
      VariadicValue.fromItems(...items),
    ];

    const networkProvider = new ApiNetworkProvider(apiAddress);
    const interaction = jewelUsdtAshFarmSmartContract.methodsExplicit.viewPositionSafetyContexts(args);
    const query = interaction.check().buildQuery();
    const queryResponse = await networkProvider.queryContract(query);
    const endpointDefinition = interaction.getEndpoint();
    const { firstValue, returnCode, returnMessage } = new ResultsParser().parseQueryResponse(queryResponse, endpointDefinition);

    if (!firstValue || !returnCode.isSuccess()) {
      toastError(returnMessage);
      return [];
    }

    const values = firstValue.valueOf();

    const decoded: JewelFarmPositionSafetyContext[] = values.map((value: any) => {
      return {
        position_id: value.position_id.toNumber(),
        value: value.value.toString(),
        lp_token_amount: value.lp_token_amount.toString(),
        debt_ratio: value.debt_ratio.toNumber(),
        liquidation_threshold: value.liquidation_threshold.toNumber(),
        to_be_liquidated: value.to_be_liquidated,
      };
    });

    return decoded;
  } catch (e) {
    console.error(e);
    toastError(ERROR_MVX_QUERY_FAILED);
    return [];
  }
};